import React from 'react';

export default function AvenorGuideHelp() {
  return (
    <section className="rounded-2xl border border-indigo-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-indigo-900">Avenor Comprehensive Guide</h2>
          <p className="mt-1 text-sm text-slate-600">End-to-end training for setup, pipeline operations, AI lead analysis, and conversion workflow.</p>
        </div>
        <a
          href="/avenor"
          className="rounded-lg bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600"
        >
          Open Avenor
        </a>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Audience</p>
          <p className="mt-1 text-sm text-slate-700">Admin, manager, and staff users with Avenor access.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Duration</p>
          <p className="mt-1 text-sm text-slate-700">45 to 60 minutes including practical walkthrough.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Outcome</p>
          <p className="mt-1 text-sm text-slate-700">Consistent lead progression, quality notes, and reliable AI-driven sales guidance.</p>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <article className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <h3 className="text-lg font-semibold text-indigo-900">Module 1: Setup and Access</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-indigo-900">
            <li>Open Avenor from the dashboard or direct route.</li>
            <li>Activate Avenor once per company if not already redeemed.</li>
            <li>Add Avenor users and choose access and permission level.</li>
            <li>Review credit pool, remaining credits, and usage.</li>
          </ul>
          <img
            src="/images/avenor-training/overview.svg"
            alt="Avenor workflow overview"
            className="mt-4 w-full rounded-lg border border-indigo-200 bg-white"
          />
        </article>

        <article className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
          <h3 className="text-lg font-semibold text-cyan-900">Module 2: Pipeline Execution</h3>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-cyan-900">
            <li>Filter by last-contacted date and progress status.</li>
            <li>Update progress values: No Reply, Interested, Sale.</li>
            <li>Add notes as append-only history objects: name, date, note.</li>
            <li>Set next-contact date and save to maintain outreach cadence.</li>
            <li>Arrange meetings and load linked meetings for timeline continuity.</li>
          </ol>
          <img
            src="/images/avenor-training/pipeline.svg"
            alt="Avenor pipeline training visual"
            className="mt-4 w-full rounded-lg border border-cyan-200 bg-white"
          />
        </article>

        <article className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <h3 className="text-lg font-semibold text-emerald-900">Module 3: AI Lead Analysis and Closing</h3>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-emerald-900">
            <li>Run Analyse Lead on qualified records.</li>
            <li>Review generated SalesReport and SalesRating.</li>
            <li>Use tips, buying signals, and objection handling to plan next action.</li>
            <li>Confirm updates are persisted to the lead and visible after refresh.</li>
            <li>Move high-intent leads to CRM and proceed to meetings/sales steps.</li>
          </ol>
          <img
            src="/images/avenor-training/analysis.svg"
            alt="Avenor analysis training visual"
            className="mt-4 w-full rounded-lg border border-emerald-200 bg-white"
          />
        </article>

        <article className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-lg font-semibold text-slate-900">Troubleshooting and QA Checklist</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
            <li>Cannot access Avenor: confirm user access is admin, manager, or staff.</li>
            <li>User add errors: verify staff has unique email and valid credentials.</li>
            <li>Analysis not reflected: check pipeline refresh and lead id-specific update path.</li>
            <li>Meeting linkage missing: verify InsightMeetingID is populated after meeting creation.</li>
            <li>Notes display issues: ensure note payloads are valid object arrays.</li>
          </ul>
        </article>

        <article className="rounded-xl border border-violet-200 bg-violet-50 p-4">
          <h3 className="text-lg font-semibold text-violet-900">Practice Lab</h3>
          <p className="mt-2 text-sm text-violet-900">Run this training sequence in order:</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-violet-900">
            <li>Add one Avenor user with read only and one with full access.</li>
            <li>Pick a contacted lead and set progress to Interested.</li>
            <li>Add one note and one next-contact entry, then verify history rendering.</li>
            <li>Run Analyse Lead and validate report and rating appear in expanded row.</li>
            <li>Arrange a meeting and confirm the linked meeting list shows the new entry.</li>
          </ol>
        </article>
      </div>
    </section>
  );
}
